import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { PieChart as PieChartIcon, Layers, Sliders, Lightbulb, ArrowLeft, RefreshCw, CheckCircle2 } from 'lucide-react';
import PageAnimator from '../../components/ui/PageAnimator';
import { Button } from '../../components/ui/button';
import MiniLoader from '../../components/MiniLoader';
import { getFamilles } from '../../features/familles/api/familleAPI';
import api from '../../api';

// Composants du module Allocation
import AllocationHeader from '../../features/allocation/components/AllocationHeader';
import AllocationCharts from '../../features/allocation/components/AllocationCharts';
import AssetClassBreakdown from '../../features/allocation/components/AssetClassBreakdown';
import TargetComparison from '../../features/allocation/components/TargetComparison';
import RebalancingAdvice from '../../features/allocation/components/RebalancingAdvice';

import {
  extractClientAssets,
  computeAllocationSummary
} from '../../features/allocation/utils/allocationMath';

export default function Allocation() {
  const location = useLocation();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [familles, setFamilles] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(location.state?.clientId || "all");
  const [selectedClientData, setSelectedClientData] = useState(null);
  const [activeTab, setActiveTab] = useState("consolidation"); // consolidation | assets | target | rebalancing

  // Actifs éditables en local pour simulations interactives
  const [clientAssets, setClientAssets] = useState(extractClientAssets(null));
  const [targetProfile, setTargetProfile] = useState("Equilibre");

  // 1. Chargement de la liste des familles
  useEffect(() => {
    const fetchFamilles = async () => {
      try {
        setIsLoading(true);
        const data = await getFamilles();
        setFamilles(data);

        // Si un clientId a été transmis via location.state
        const targetId = location.state?.clientId;
        const targetType = location.state?.clientType || "famille";
        if (targetId) {
          setSelectedClientId(targetId);
          await loadClientDetails(targetId, data, targetType);
        } else {
          // Mode portefeuille global par défaut
          loadAggregatePortfolio(data);
        }
      } catch (err) {
        console.error("Erreur chargement des familles :", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFamilles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.clientId, location.state?.clientType]);

  // 2. Chargement des détails d'un client spécifique
  const loadClientDetails = async (clientId, clientList = familles, clientType = location.state?.clientType || "famille") => {
    try {
      if (clientType === "societe") {
        const [resSociete, resTree] = await Promise.all([
          api.get(`/societes/${clientId}`).catch(() => null),
          api.get(`/societes/${clientId}/tree`).catch(() => null)
        ]);
        const societeData = resSociete?.data?.societe || resSociete?.data;
        const treeData = resTree?.data || null;

        const fullSociete = societeData || {
          id: clientId,
          denomination: location.state?.clientName,
          formeJuridique: location.state?.formeJuridique,
          tresorerieDisponible: location.state?.tresorerie,
          placementsEntreprise: location.state?.placements,
          valorisationEstimee: location.state?.valorisation,
          dettes: location.state?.dettes
        };

        setSelectedClientData(fullSociete);
        const extracted = extractClientAssets(fullSociete, treeData, "societe");
        setClientAssets(extracted);
        setTargetProfile(extracted.profilRisque);
        return;
      }

      // Chargement en parallèle de la famille et de l'arbre
      const [resFamille, resTree] = await Promise.all([
        api.get(`/familles/${clientId}`).catch(() => null),
        api.get(`/familles/${clientId}/tree`).catch(() => null)
      ]);
      const fullClient = resFamille?.data || clientList.find(f => f.id === clientId || f._id === clientId);
      const treeData = resTree?.data || null;
      
      setSelectedClientData(fullClient);
      const extracted = extractClientAssets(fullClient, treeData, "famille");
      setClientAssets(extracted);
      setTargetProfile(extracted.profilRisque);
    } catch (err) {
      console.error("Erreur récupération client:", err);
    }
  };

  // 3. Calcul agrégé global de tous les clients
  const loadAggregatePortfolio = (clientList) => {
    let aggLiquidites = { compteCourant: 0, livrets: 0, pelCel: 0, autres: 0 };
    let aggPlacements = { assuranceVieEuros: 0, assuranceVieUC: 0, pea: 0, pee: 0, compteTitre: 0, cryptoMetaux: 0 };
    let aggImmobilier = { residencePrincipale: 0, residenceSecondaire: 0, locatif: 0, scpi: 0, dettesImmo: 0 };
    let aggAlternatif = { privateEquity: 0, dettePrivee: 0, participationsHipparchus: 0 };
    let aggProfessionnel = { valorisationParts: 0, compteCourantAssocie: 0 };

    clientList.forEach(c => {
      const a = extractClientAssets(c);
      Object.keys(a.liquidites).forEach(k => aggLiquidites[k] += a.liquidites[k]);
      Object.keys(a.placements).forEach(k => aggPlacements[k] += a.placements[k]);
      Object.keys(a.immobilier).forEach(k => aggImmobilier[k] += a.immobilier[k]);
      Object.keys(a.alternatif).forEach(k => aggAlternatif[k] += a.alternatif[k]);
      Object.keys(a.professionnel).forEach(k => aggProfessionnel[k] += a.professionnel[k]);
    });

    setClientAssets({
      liquidites: aggLiquidites,
      placements: aggPlacements,
      immobilier: aggImmobilier,
      alternatif: aggAlternatif,
      professionnel: aggProfessionnel,
      profilRisque: "Equilibre",
      horizonAnnees: 8
    });
    setTargetProfile("Equilibre");
    setSelectedClientData(null);
  };

  // Gestion du changement de sélection client
  const handleSelectClient = (newId) => {
    setSelectedClientId(newId);
    if (newId === "all") {
      loadAggregatePortfolio(familles);
    } else {
      const type = (newId === location.state?.clientId && location.state?.clientType === "societe") ? "societe" : "famille";
      loadClientDetails(newId, familles, type);
    }
  };

  // Réinitialisation des actifs
  const handleResetAssets = () => {
    if (selectedClientId === "all") {
      loadAggregatePortfolio(familles);
    } else {
      const type = (selectedClientId === location.state?.clientId && location.state?.clientType === "societe") ? "societe" : "famille";
      loadClientDetails(selectedClientId, familles, type);
    }
  };

  // Calcul du résumé de l'allocation
  const summary = computeAllocationSummary(clientAssets, targetProfile);

  const tabs = [
    { id: "consolidation", label: "Vue Consolidée & KPIs", icon: PieChartIcon },
    { id: "assets", label: "Poches d'Actifs & Détail", icon: Sliders },
    { id: "target", label: "Allocation Cible & Écarts", icon: Layers },
    { id: "rebalancing", label: "Arbitrages & Injection", icon: Lightbulb },
  ];

  return (
    <div className="p-4 sm:p-8 bg-workspace min-h-screen pb-24">
      <PageAnimator>
        {/* Header Institutionnel */}
        <div
          className="
            relative z-20
            flex shrink-0 flex-col
            gap-6
            overflow-visible
            workspace-card
            bg-cover bg-center bg-no-repeat
            p-5 sm:p-8
            mb-8
            xl:flex-row xl:items-center xl:justify-between
            rounded-3xl
            border border-workspace
            shadow-hp-md
          "
          style={{ backgroundImage: "url('/assets/backgrounds/lighthouse_bg.png')" }}
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-0 rounded-3xl bg-overlay"
          />

          <div className="relative z-10 flex min-w-0 shrink-0 flex-col items-start gap-4 sm:flex-row sm:items-center sm:gap-5">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-secondary text-white shadow-hp-md border border-white/20">
              <PieChartIcon className="h-7 w-7" />
            </div>

            <div className="min-w-0">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
                  Simulateur d'Allocation Patrimoniale
                </h1>
                {selectedClientData && (
                  <span className="hidden sm:inline-flex items-center gap-1 px-3 py-0.5 rounded-full bg-white/10 text-white text-xs font-semibold backdrop-blur-md border border-white/20">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    {location.state?.clientType === "societe" ? "Société : " : "Dossier Client : "}
                    {selectedClientData.denomination || selectedClientData.raisonSociale || selectedClientData.nomFamille || selectedClientData.nom || "Actif"}
                  </span>
                )}
              </div>
              <p className="mt-1.5 text-sm text-white/80 max-w-2xl">
                Bac à sable d'arbitrage et d'ingénierie financière : modélisez des scénarios tactiques, projetez des injections de capitaux et préparez les propositions d'allocation.
              </p>
            </div>
          </div>

          <div className="relative z-10 flex items-center gap-3">
            {location.state?.fromPilotage ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate('/conseil/allocations')}
                className="gap-1.5 text-xs bg-white/10 hover:bg-white/20 border-white/20 text-white backdrop-blur-md"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Retour au Pilotage
              </Button>
            ) : location.state?.clientId ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate(location.state?.clientType === "societe" ? `/prospects/societes/${location.state.clientId}` : `/prospects/familles/${location.state.clientId}`)}
                className="gap-1.5 text-xs bg-white/10 hover:bg-white/20 border-white/20 text-white backdrop-blur-md"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Retour au Dossier
              </Button>
            ) : null}
            <Button
              variant="outline"
              size="sm"
              onClick={handleResetAssets}
              className="gap-1.5 text-xs bg-white/10 hover:bg-white/20 border-white/20 text-white backdrop-blur-md"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Actualiser
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="py-24 flex items-center justify-center">
            <MiniLoader />
          </div>
        ) : (
          <>
            {/* Sélecteur Client & KPIs Principaux */}
            <AllocationHeader
              clients={
                (location.state?.clientType === "societe" && location.state?.clientId)
                  ? [{
                      id: location.state.clientId,
                      denomination: selectedClientData?.denomination || selectedClientData?.raisonSociale || location.state.clientName,
                      reference: selectedClientData?.formeJuridique || location.state.formeJuridique || 'Société',
                      isSociete: true
                    }, ...familles]
                  : familles
              }
              selectedClientId={selectedClientId}
              onSelectClient={handleSelectClient}
              summary={summary}
              targetProfile={targetProfile}
              onChangeProfile={setTargetProfile}
              horizonAnnees={clientAssets.horizonAnnees}
              onChangeHorizon={(h) => setClientAssets(prev => ({ ...prev, horizonAnnees: h }))}
            />

        {/* Barre d'Onglets Segmentés */}
        <div className="flex border-b border-workspace mt-8 mb-6 overflow-x-auto gap-2 scrollbar-none">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold whitespace-nowrap transition-all border-b-2 ${
                  isActive
                    ? "border-secondary text-primary"
                    : "border-transparent text-subtle hover:text-primary hover:border-[var(--hp-color-border-strong)]"
                }`}
              >
                <Icon className={`w-4 h-4 ${isActive ? "text-secondary" : "text-subtle"}`} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Contenu dynamique par onglet */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
          >
            {activeTab === "consolidation" && (
              <AllocationCharts summary={summary} />
            )}

            {activeTab === "assets" && (
              <AssetClassBreakdown
                assets={clientAssets}
                onChangeAssets={setClientAssets}
                onReset={handleResetAssets}
              />
            )}

            {activeTab === "target" && (
              <TargetComparison summary={summary} />
            )}

            {activeTab === "rebalancing" && (
              <RebalancingAdvice summary={summary} />
            )}
          </motion.div>
        </AnimatePresence>
        </>
      )}
      </PageAnimator>
    </div>
  );
}
